import React, { useState, useEffect } from 'react';
import { supabase } from '../util/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useApiContext } from '../context/ApiContext';
import Spin from '../components/Spin';

export default function MyInfo() {
  const navigate = useNavigate();
  const { session, isLoading: authLoading } = useAppContext();
  const { api, isLoading: apiLoading } = useApiContext();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [cancelRequested, setCancelRequested] = useState(false);
  const [history, setHistory] = useState([]); // 구매 내역
  const [freeTrialInfo, setFreeTrialInfo] = useState(null); // 무료체험 정보

  useEffect(() => {
    async function fetchData() {
      if (!session?.user) {
        // 로그인 정보가 없으면 리디렉션
        navigate('/');
        return;
      }

      setLoading(true);
      try {
        // 1) 현재 유저 구독 정보 조회 (유료 구독)
        const { data: subs, error: subsError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('status', 'active')  // 활성 구독만 가져오기
          .not('plan_name', 'eq', 'FREE_TRIAL') // 무료체험이 아닌 구독만
          .single();

        if (subsError) {
          // PGRST116: 결과가 없음 - 이는 정상적인 경우임 (구독이 없는 경우)
          if (subsError.code !== 'PGRST116') { 
            console.error('Subscription fetch error:', subsError);
          }
        } else {
          setSubscription(subs);
          setCancelRequested(subs?.cancellation_requested || false);
        }

        // 2) 무료체험 정보 조회 (새로운 방식: subscriptions 테이블에서)
        const { data: trialData, error: trialError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('plan_name', 'FREE_TRIAL')
          .eq('status', 'trialing')
          .single();
        
        if (trialError) {
          // 무료체험이 없는 것은 정상적인 경우
          if (trialError.code !== 'PGRST116') {
            console.error('Free trial fetch error:', trialError);
          }
        } else {
          setFreeTrialInfo(trialData);
        }

        // 3) 구매 내역 가져오기
        const { data: historyData, error: historyError } = await supabase
          .from('payment_history')
          .select('*')
          .eq('user_id', session.user.id)
          .order('paid_at', { ascending: false })
          .limit(20);
          
        if (historyError) {
          console.error('History fetch error:', historyError);
        } else {
          setHistory(historyData || []);
        }
      } catch (error) {
        console.error('Fetch Data Error:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [session, navigate]);

  // 구독 취소 요청 함수
  const handleCancellationRequest = async () => {
    try {
      if (!session?.user?.id) {
        alert('로그인이 필요한 서비스입니다.');
        return;
      }

      const { data, error } = await api.requestCancelSubscription();
      
      if (error) {
        alert(error.message || '구독 취소 요청 중 오류가 발생했습니다.');
        return;
      }
      
      setCancelRequested(true);
      alert('구독 취소 요청이 접수되었습니다.');
    } catch (error) {
      console.error('구독 취소 요청 중 오류:', error);
      alert('서버 연결 중 오류가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  const isLoading = loading || authLoading || apiLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spin size={32} /><span className="ml-3 text-lg sm:text-3xl text-gray-700">데이터를 불러오는 중입니다...</span>
      </div>
    );
  }

  // 로그인이 안 되어 있으면 홈으로
  if (!session?.user) {
    return null;
  }

  // --- 실제 화면 렌더링 ---
  // 무료체험이 활성화되어 있고 구독이 없는 경우
  const hasFreeTrialActive = freeTrialInfo && freeTrialInfo.status === 'trialing';
  
  // 날짜 변환용 헬퍼 (timestamp -> YYYY.MM.DD 등)
  const formatDate = (ts) => {
    if (!ts) return '정보 없음';
    const date = new Date(ts);
    
    // 한국식 날짜 포맷: YYYY년 MM월 DD일
    return `${date.getFullYear()}년 ${(date.getMonth() + 1).toString().padStart(2, '0')}월 ${date.getDate().toString().padStart(2, '0')}일`;
  };

  // 무료체험 유효 여부 확인
  const isFreeTrialValid = () => {
    if (!freeTrialInfo) return false;
    const now = new Date();
    const endDate = new Date(freeTrialInfo.trial_end);
    return now <= endDate;
  };

  return (
    <div className="flex flex-col items-center p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-8">플랜 관리</h1>

      {hasFreeTrialActive && isFreeTrialValid() ? (
        // 무료체험 영역
        <div className="bg-gray-50 rounded-lg p-4 md:p-6 w-full max-w-2xl mb-4 md:mb-8 shadow-lg">
          <p className="text-base md:text-lg mb-2 md:mb-4">무료체험 플랜을 이용중입니다.</p>
          <p className="text-sm md:text-base text-gray-600 mb-4">
            이용기간 : {formatDate(freeTrialInfo.current_period_start)} ~ {formatDate(freeTrialInfo.trial_end)}
          </p>
          <button
            onClick={() => navigate('/price')}
            className="bg-green-600 text-white px-4 md:px-6 py-2 rounded-md hover:bg-green-700 transition-colors text-sm md:text-base"
          >
            플랜 변경 (유료가입)
          </button>
        </div>
      ) : subscription && subscription.status === 'active' ? (
        // 유료 구독 영역
        <div className="bg-gray-50 rounded-lg p-4 md:p-6 w-full max-w-2xl mb-4 md:mb-8 shadow-lg">
          <p className="text-base md:text-lg mb-2 md:mb-4">
            <strong>{subscription.plan_name}</strong> 플랜 이용중
          </p>
          <p className="text-sm md:text-base text-gray-600 mb-4">
            {subscription.current_period_start && subscription.current_period_end ? (
              <>이용기간: {formatDate(subscription.current_period_start)} ~ {formatDate(subscription.current_period_end)}</>
            ) : (
              <>가입일: {formatDate(subscription.created_at)}</>
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              onClick={() => navigate('/price')}
              className="bg-green-600 text-white px-4 md:px-6 py-2 rounded-md hover:bg-green-700 transition-colors text-sm md:text-base"
            >
              멤버십 변경하기
            </button>

            {/* 취소 요청 버튼 / 요청 완료 메시지 */}
            {cancelRequested ? (
              <p className="mt-2 md:mt-4 text-red-600 text-sm md:text-base">취소 요청이 접수되었습니다.</p>
            ) : (
              <button
                onClick={handleCancellationRequest}
                className="bg-orange-500 text-white px-4 md:px-6 py-2 rounded-md hover:bg-red-600 transition-colors text-sm md:text-base"
              >
                구독 취소 요청
              </button>
            )}
          </div>
        </div>
      ) : (
        // 멤버십 없음 영역
        <div className="bg-gray-50 rounded-lg p-4 md:p-6 w-full max-w-2xl mb-4 md:mb-8 shadow-lg">
          <p className="text-base md:text-lg mb-2 md:mb-4">현재 이용중인 플랜이 없습니다.</p>
          <button
            onClick={() => navigate('/price')}
            className="bg-green-600 text-white px-4 md:px-6 py-2 rounded-md hover:bg-green-700 transition-colors text-sm md:text-base"
          >
            플랜 살펴보기
          </button>
        </div>
      )}

      {/* 구매 내역 */}
      <div className="w-full max-w-2xl">
        <h2 className="text-lg md:text-xl font-semibold mb-2 md:mb-4">
          구매 내역
        </h2>
        <div className="bg-white rounded-lg overflow-hidden shadow-md">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs md:text-sm font-medium">결제일</th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs md:text-sm font-medium">멤버십</th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs md:text-sm font-medium">금액</th>
                </tr>
              </thead>
              <tbody>
                {history.length > 0 ? (
                  history.map((item) => (
                    <tr key={item.id} className="border-b last:border-0">
                      <td className="px-3 md:px-6 py-2 md:py-3 text-left text-xs md:text-sm">
                        {formatDate(item.paid_at)}
                      </td>
                      <td className="px-3 md:px-6 py-2 md:py-3 text-left text-xs md:text-sm">
                        {item.plan_name}
                      </td>
                      <td className="px-3 md:px-6 py-2 md:py-3 text-left text-xs md:text-sm">
                        $ {item.amount}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-3 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm text-gray-500"
                    >
                      아직 결제 내역이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
